import { useEffect, Suspense, lazy } from "react";
import Hero from "./sections/Hero";
import About from "./sections/About";
import LatestWork from "./sections/LatestWork";
import LatestWorkPricing from "./components/LatestWorkPricing";
import TidyCall from "./components/TidyCall";
import LazyMount from "./components/LazyMount";
import { initOverlayLoader, closeOverlayLoader } from "./loader";
import { initCursor } from "./cursor";
import { initPerformanceMonitoring } from "./utils/performance";
import { useRouteLocation } from "./hooks/useRouteLocation";
import { useScrollBehavior } from "./hooks/useScrollBehavior";
import { PageLayout, ErrorPageLayout } from "./components/PageLayout";

const WorkDetail = lazy(() => import("./sections/WorkDetail"));
const Works = lazy(() => import("./sections/Works"));

const HomePageContent = () => (
  <main className="home-surface">
    <About />
    <LatestWork />
    <LatestWorkPricing />
    <TidyCall />
  </main>
);

/**
 * Main application component with client-side routing.
 * Initializes animations, scroll behavior, and cursor effects.
 */
const App = () => {
  const { location, route } = useRouteLocation();
  const shouldSkipScroll = route.type === "work-detail" || route.type === "works";

  useScrollBehavior(location, shouldSkipScroll);

  // Initialize animations and effects once
  useEffect(() => {
    if (typeof window === "undefined") return;

    initOverlayLoader();
    const cleanupCursor = initCursor();
    const cleanupPerf = initPerformanceMonitoring();

    // Fallback: Close loader after 4 seconds if it's still open
    // This handles cases where HeroScene/Astronaut doesn't mount
    const loaderFallbackTimer = setTimeout(() => {
      closeOverlayLoader();
    }, 4000);

    return () => {
      clearTimeout(loaderFallbackTimer);
      cleanupCursor?.();
      cleanupPerf?.();
    };
  }, []);

  // Route rendering
  switch (route.type) {
    case "work-detail":
      return (
        <PageLayout
          loadingMessage="Loading case study…"
          fallbackMessage="We couldn't load that case study right now."
        >
          <WorkDetail id={route.id} />
        </PageLayout>
      );

    case "works":
      return (
        <PageLayout
          loadingMessage="Loading works…"
          fallbackMessage="Works failed to load — please refresh."
        >
          <Works />
        </PageLayout>
      );

    case "not-found":
      return (
        <ErrorPageLayout
          title="Page not found"
          message="The page you are looking for doesn't exist."
        />
      );

    case "home":
    default:
      return (
        <PageLayout loadingMessage="Loading…" fallbackMessage="Failed to load.">
          <Hero />
          <HomePageContent />
        </PageLayout>
      );
  }
};

export default App;
